import { HttpClient } from '@angular/common/http';
import { Observable, finalize, map, shareReplay } from 'rxjs';
import { AbstractService } from '../../abstractservice';
import { AuthService } from '../../auth.service';
import { FeatureFlagService } from '../../feature-flag.service';
import { Hydratable, JsonMappable } from '../../jsonmapper.service';

export class CacheRepository<TIdentifier, TCacheable extends (JsonMappable<TCacheable> | Hydratable<TCacheable>)> extends AbstractService {

    protected cache: Map<TIdentifier, TCacheable>;
    private typeInstance: TCacheable;

    // Tracks fetches already dispatched but not yet resolved, keyed by id - see claimInFlight/
    // registerInFlight below. Separate from `cache`, which only ever holds settled values.
    private inFlight: Map<TIdentifier, Observable<TCacheable>>;

    constructor(
        protected identiferPropertySelector: (item: TCacheable) => TIdentifier,
        protected http: HttpClient,
        auth: AuthService,
        featureFlagService: FeatureFlagService,
        private type: new () => TCacheable) {

        super(auth, featureFlagService);
        this.typeInstance = new type();
    }

    public cacheItems(...items: TCacheable[]): TCacheable[] {
        if (!items || !items.length) return null;
        this.initCache();
        items.forEach(item => this.cache.set(this.identiferPropertySelector(item), item));
        return items;
    }

    // Splits `ids` into those with a fetch already dispatched and not yet settled (`attached` -
    // each mapped to that same fetch's own per-id result) versus those needing a fresh fetch
    // (`stillNeeded`). Call this instead of firing a remote request directly whenever more than
    // one caller for the same id can plausibly race - see registerInFlight for the other half.
    // Without this, two concurrent callers for the same not-yet-cached id would each miss the
    // cache independently (it's only ever populated after a fetch resolves) and each fire their
    // own request, landing two separate instances of the same logical entity instead of one
    // shared one - defeating the whole point of a shared cache for exactly the case (a burst of
    // near-simultaneous lookups) where sharing matters most.
    public claimInFlight(ids: TIdentifier[]): { attached: Map<TIdentifier, Observable<TCacheable>>; stillNeeded: TIdentifier[] } {
        const attached = new Map<TIdentifier, Observable<TCacheable>>();
        const stillNeeded: TIdentifier[] = [];
        ids.forEach(id => {
            const existing = this.inFlight?.get(id);
            if (existing) {
                attached.set(id, existing);
            } else {
                stillNeeded.push(id);
            }
        });
        return { attached, stillNeeded };
    }

    // Registers `rawFetch$` (a single already-in-progress fetch covering every id in `ids`,
    // not yet subscribed to) as the in-flight request for each of those ids, and returns a
    // Map of per-id Observables derived from it. Must be called synchronously, in the same
    // tick as the claimInFlight() call that found none of these ids in flight yet - a
    // concurrent caller checking a tick later needs to reliably find this registration rather
    // than race it and dispatch a duplicate fetch.
    //
    // `rawFetch$` is shared (multicast) so every id's derived Observable - including this
    // caller's own - and any later attacher subscribe to the exact same underlying HTTP call
    // and mapping/hydration pass, not independent copies of it: this is what makes concurrent
    // lookups for the same id resolve to the same object instance, not just the same data.
    // Cleanup (removing these ids from `inFlight`) happens once, via `finalize`, when the
    // underlying fetch itself settles (success or failure) - not per-subscriber, and not on a
    // timer - so a settled id becomes immediately eligible for a fresh fetch rather than being
    // stuck replaying a stale in-flight entry.
    public registerInFlight(ids: TIdentifier[], rawFetch$: Observable<TCacheable[]>): Map<TIdentifier, Observable<TCacheable>> {
        const shared$ = rawFetch$.pipe(
            finalize(() => ids.forEach(id => this.inFlight?.delete(id))),
            shareReplay(1));

        this.initInFlight();
        const perId = new Map<TIdentifier, Observable<TCacheable>>();
        ids.forEach(id => {
            const forThisId$ = shared$.pipe(map(items => items.find(item => this.identiferPropertySelector(item) === id) ?? null));
            this.inFlight.set(id, forThisId$);
            perId.set(id, forThisId$);
        });
        return perId;
    }

    public uncacheItems(...items: TCacheable[]) {
        if (!items || !items.length || !this.cache) return;
        items.forEach(item => this.cache.delete(this.identiferPropertySelector(item)));
    }

    public getFromCache(...ids: TIdentifier[]): CacheResult<TIdentifier, TCacheable> {
        if (!this.cache) {
            return { retrieved: [], failed: ids };
        }

        var splitByCache = ids
            .map(id => ({ id: id, item: this.cache.get(id) }))
            .reduce<{ cached: TCacheable[], missed: TIdentifier[] }>(
                (accumulator, cachePair) => {
                    (cachePair.item ? accumulator.cached : accumulator.missed).push(<any>(cachePair.item ?? cachePair.id));
                    return accumulator;
                },
                { cached: [], missed: [] });
        return { retrieved: splitByCache.cached, failed: splitByCache.missed };
    }

    public getId(jsonShape: any) {
        return this.identiferPropertySelector(jsonShape);
    }

    private initCache() {
        if (this.cache) return;
        this.cache = new Map();
    }

    private initInFlight() {
        if (this.inFlight) return;
        this.inFlight = new Map();
    }
}

export interface CacheResult<TIdentifier, TCacheable> {
    retrieved: TCacheable[];
    failed: TIdentifier[];
}

